import { create } from "@bufbuild/protobuf";
import { timestampNow } from "@bufbuild/protobuf/wkt";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    type InfiniteData,
    type QueryClient,
    type QueryKey,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";

import type { GitpodAPI } from "@/api";
import { useToast } from "@/components/flexkit/toast";
import { useEventInterest } from "@/hooks/use-event-interest";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { formatError } from "@/utils/errors";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import type { ListNotificationsResponse, Notification } from "gitpod-next-api/gitpod/v1/notification_pb";
import { PaginationRequestSchema } from "gitpod-next-api/gitpod/v1/pagination_pb";

export const NOTIFICATION_LIST_KEY = keyWithPrincipal(["notification-list"]);

type NotificationPages = InfiniteData<ListNotificationsResponse, string>;
type NotificationCacheSnapshot = [QueryKey, NotificationPages | undefined][];

export function useNotificationList(pageSize = 25) {
    const api = useGitpodAPI();

    useEventInterest({
        resourceType: ResourceType.NOTIFICATION,
    });

    const query = useInfiniteQuery({
        queryKey: [...NOTIFICATION_LIST_KEY, pageSize],
        queryFn: async ({ pageParam = "" }) => {
            const res = await api.notificationService.listNotifications({
                pagination: create(PaginationRequestSchema, { pageSize, token: pageParam }),
            });
            return res;
        },
        initialPageParam: "",
        getNextPageParam: (lastPage) => lastPage.pagination?.nextToken || undefined,
        refetchInterval: 1_000 * 60 * 5, // 5 minutes — fallback if event stream misses updates
        refetchIntervalInBackground: true,
    });

    const pages = query.data?.pages;
    const latestPage = pages?.[pages.length - 1];

    return {
        data: pages?.flatMap((p) => p.notifications),
        isLoading: query.isLoading,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        unreadCount: latestPage?.unreadCount ?? 0,
        totalCount: latestPage?.totalCount ?? 0,
    };
}

// ---------------------------------------------------------------------------
// Shared optimistic update helpers
// ---------------------------------------------------------------------------

type PageTransformResult = {
    notification: Notification | undefined; // undefined = remove
    wasUnread: boolean;
};

/**
 * Applies an optimistic update to all notification list caches.
 *
 * @param transform - Called for each targeted notification. Return the updated
 *   notification (or undefined to remove it) and whether it was unread before
 *   the transform. Counter adjustments are derived from these results.
 */
function applyOptimisticUpdate(
    queryClient: QueryClient,
    notificationIds: string[],
    transform: (n: Notification) => PageTransformResult,
): { snapshot: NotificationCacheSnapshot } {
    const snapshot: NotificationCacheSnapshot = queryClient.getQueriesData<NotificationPages>({
        queryKey: NOTIFICATION_LIST_KEY,
    });

    const idSet = new Set(notificationIds);
    queryClient.setQueriesData<NotificationPages>({ queryKey: NOTIFICATION_LIST_KEY }, (old) => {
        if (!old) return old;

        let deltaUnread = 0;
        let deltaTotal = 0;

        const transformedPages = old.pages.map((page) => {
            const notifications: Notification[] = [];
            for (const n of page.notifications) {
                if (!idSet.has(n.id)) {
                    notifications.push(n);
                    continue;
                }
                const { notification, wasUnread } = transform(n);
                if (notification) {
                    const isNowUnread = !notification.readAt;
                    if (wasUnread && !isNowUnread) deltaUnread--;
                    if (!wasUnread && isNowUnread) deltaUnread++;
                    notifications.push(notification);
                } else {
                    deltaTotal--;
                    if (wasUnread) deltaUnread--;
                }
            }
            return { ...page, notifications };
        });

        const pages = transformedPages.map((page) => ({
            ...page,
            unreadCount: Math.max(0, page.unreadCount + deltaUnread),
            totalCount: Math.max(0, page.totalCount + deltaTotal),
        }));

        return { ...old, pages };
    });

    return { snapshot };
}

function restoreSnapshot(queryClient: QueryClient, snapshot: NotificationCacheSnapshot) {
    for (const [key, data] of snapshot) {
        queryClient.setQueryData(key, data);
    }
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Max notification IDs per API call, enforced by proto validation. */
export const NOTIFICATION_BATCH_SIZE = 25;

/** Split an array into chunks of at most `size` elements. */
export function chunked<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

/**
 * Settles all batched API calls and returns the IDs from failed batches.
 * Preserves the original rejection reason for error reporting.
 */
function settledBatchFailures(
    batches: string[][],
    results: PromiseSettledResult<unknown>[],
): { failedIds: string[]; firstError: unknown } {
    const failedIds: string[] = [];
    let firstError: unknown;
    for (let i = 0; i < results.length; i++) {
        if (results[i].status === "rejected") {
            failedIds.push(...batches[i]);
            firstError ??= (results[i] as PromiseRejectedResult).reason;
        }
    }
    return { failedIds, firstError };
}

type BatchResult = { failedIds: string[]; firstError: unknown };

/**
 * Handles partial batch failures: restores the full optimistic snapshot,
 * then re-applies the transform for only the succeeded IDs so the UI
 * stays in sync with what the server actually processed.
 */
function handlePartialFailure(
    queryClient: QueryClient,
    toast: ReturnType<typeof useToast>["toast"],
    { failedIds, firstError }: BatchResult,
    allIds: string[],
    context: { snapshot: NotificationCacheSnapshot } | undefined,
    transform: (n: Notification) => PageTransformResult,
    errorLabel: string,
) {
    if (failedIds.length === 0) return;
    if (context?.snapshot) {
        restoreSnapshot(queryClient, context.snapshot);
        const failedSet = new Set(failedIds);
        const succeededIds = allIds.filter((id) => !failedSet.has(id));
        if (succeededIds.length > 0) {
            applyOptimisticUpdate(queryClient, succeededIds, transform);
        }
    }
    toast({
        title: `Failed to ${errorLabel} ${failedIds.length} notifications`,
        description: firstError ? formatError(firstError) : undefined,
    });
}

export function useMarkNotificationsRead() {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const transform = (n: Notification): PageTransformResult => ({
        notification: n.readAt ? n : { ...n, readAt: timestampNow() },
        wasUnread: !n.readAt,
    });

    return useMutation({
        mutationFn: async (notificationIds: string[]) => {
            const batches = chunked(notificationIds, NOTIFICATION_BATCH_SIZE);
            const results = await Promise.allSettled(
                batches.map((ids) => api.notificationService.markNotificationsRead({ notificationIds: ids })),
            );
            return settledBatchFailures(batches, results);
        },
        onMutate: async (notificationIds) => {
            await queryClient.cancelQueries({ queryKey: NOTIFICATION_LIST_KEY });
            return applyOptimisticUpdate(queryClient, notificationIds, transform);
        },
        onSuccess: (result, ids, context) =>
            handlePartialFailure(queryClient, toast, result, ids, context, transform, "mark as read"),
        onError: (error, _ids, context) => {
            if (context?.snapshot) {
                restoreSnapshot(queryClient, context.snapshot);
            }
            toast({ title: "Failed to mark notifications as read", description: formatError(error) });
        },
    });
}

export function useMarkNotificationsUnread() {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const transform = (n: Notification): PageTransformResult => ({
        notification: n.readAt ? { ...n, readAt: undefined } : n,
        wasUnread: !n.readAt,
    });

    return useMutation({
        mutationFn: async (notificationIds: string[]) => {
            const batches = chunked(notificationIds, NOTIFICATION_BATCH_SIZE);
            const results = await Promise.allSettled(
                batches.map((ids) => api.notificationService.markNotificationsUnread({ notificationIds: ids })),
            );
            return settledBatchFailures(batches, results);
        },
        onMutate: async (notificationIds) => {
            await queryClient.cancelQueries({ queryKey: NOTIFICATION_LIST_KEY });
            return applyOptimisticUpdate(queryClient, notificationIds, transform);
        },
        onSuccess: (result, ids, context) =>
            handlePartialFailure(queryClient, toast, result, ids, context, transform, "mark as unread"),
        onError: (error, _ids, context) => {
            if (context?.snapshot) {
                restoreSnapshot(queryClient, context.snapshot);
            }
            toast({ title: "Failed to mark notifications as unread", description: formatError(error) });
        },
    });
}

export function useDeleteNotifications() {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const transform = (n: Notification): PageTransformResult => ({
        notification: undefined,
        wasUnread: !n.readAt,
    });

    return useMutation({
        mutationFn: async (notificationIds: string[]) => {
            const batches = chunked(notificationIds, NOTIFICATION_BATCH_SIZE);
            const results = await Promise.allSettled(
                batches.map((ids) => api.notificationService.deleteNotifications({ notificationIds: ids })),
            );
            return settledBatchFailures(batches, results);
        },
        onMutate: async (notificationIds) => {
            await queryClient.cancelQueries({ queryKey: NOTIFICATION_LIST_KEY });
            return applyOptimisticUpdate(queryClient, notificationIds, transform);
        },
        onSuccess: (result, ids, context) =>
            handlePartialFailure(queryClient, toast, result, ids, context, transform, "delete"),
        onError: (error, _ids, context) => {
            if (context?.snapshot) {
                restoreSnapshot(queryClient, context.snapshot);
            }
            toast({ title: "Failed to delete notifications", description: formatError(error) });
        },
    });
}

// ---------------------------------------------------------------------------
// Event stream helpers
// ---------------------------------------------------------------------------

/** Fetch a single notification, logging non-404 errors. Returns undefined on failure. */
async function fetchNotification(api: GitpodAPI, notificationId: string): Promise<Notification | undefined> {
    try {
        const res = await api.notificationService.getNotification({ notificationId });
        return res.notification;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to fetch notification", notificationId, error);
        }
    }
}

/** Returns true if the notification ID exists in any cached page. */
function isNotificationInCache(client: QueryClient, notificationId: string): boolean {
    const entries = client.getQueriesData<NotificationPages>({ queryKey: NOTIFICATION_LIST_KEY });
    for (const [, data] of entries) {
        if (!data) continue;
        for (const page of data.pages) {
            if (page.notifications.some((n) => n.id === notificationId)) {
                return true;
            }
        }
    }
    return false;
}

// ---------------------------------------------------------------------------
// UPDATE event coalescing
// ---------------------------------------------------------------------------

// When many UPDATE events arrive in a short window (e.g. bulk mark-read),
// we accumulate IDs and decide how to refresh:
//   ≤ COALESCE_THRESHOLD  → individual GetNotification per ID
//   > COALESCE_THRESHOLD  → single list invalidation
export const COALESCE_THRESHOLD = 10;
export const COALESCE_WINDOW_MS = 200;

let pendingUpdateIds = new Set<string>();
let coalescedApi: GitpodAPI | undefined;
let coalescedClient: QueryClient | undefined;
let coalesceTimer: ReturnType<typeof setTimeout> | undefined;

async function fetchAndApplyUpdate(api: GitpodAPI, client: QueryClient, notificationId: string) {
    const notification = await fetchNotification(api, notificationId);
    if (!notification) return;

    client.setQueriesData<NotificationPages>({ queryKey: NOTIFICATION_LIST_KEY }, (old) => {
        if (!old) return old;
        let deltaUnread = 0;

        const transformedPages = old.pages.map((page) => {
            const notifications = page.notifications.map((n) => {
                if (n.id !== notificationId) return n;
                const wasUnread = !n.readAt;
                const isNowUnread = !notification.readAt;
                if (wasUnread && !isNowUnread) deltaUnread--;
                if (!wasUnread && isNowUnread) deltaUnread++;
                return notification;
            });
            return { ...page, notifications };
        });

        const pages = transformedPages.map((page) => ({
            ...page,
            unreadCount: Math.max(0, page.unreadCount + deltaUnread),
        }));

        return { ...old, pages };
    });
}

function flushCoalescedUpdates() {
    const ids = pendingUpdateIds;
    const api = coalescedApi;
    const client = coalescedClient;
    pendingUpdateIds = new Set();
    coalescedApi = undefined;
    coalescedClient = undefined;
    coalesceTimer = undefined;

    if (!api || !client || ids.size === 0) return;

    if (ids.size > COALESCE_THRESHOLD) {
        void client.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
    } else {
        for (const id of ids) {
            void fetchAndApplyUpdate(api, client, id);
        }
    }
}

/** Flush pending coalesced updates immediately. Exposed for testing. */
export function flushPendingNotificationUpdates() {
    if (coalesceTimer !== undefined) {
        clearTimeout(coalesceTimer);
    }
    flushCoalescedUpdates();
}

// ---------------------------------------------------------------------------
// Event stream handler
// ---------------------------------------------------------------------------

export const handleNotificationEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.operation) {
        case ResourceOperation.DELETE: {
            applyOptimisticUpdate(client, [evt.resourceId], (n) => ({
                notification: undefined,
                wasUnread: !n.readAt,
            }));
            break;
        }
        case ResourceOperation.CREATE: {
            const notification = await fetchNotification(api, evt.resourceId);
            if (!notification) {
                break;
            }
            if (isNotificationInCache(client, notification.id)) {
                break;
            }
            client.setQueriesData<NotificationPages>({ queryKey: NOTIFICATION_LIST_KEY }, (old) => {
                if (!old) return old;
                const deltaUnread = notification.readAt ? 0 : 1;
                const pages = old.pages.map((page, pageIndex) => ({
                    ...page,
                    notifications: pageIndex === 0 ? [notification, ...page.notifications] : page.notifications,
                    unreadCount: page.unreadCount + deltaUnread,
                    totalCount: page.totalCount + 1,
                }));
                return { ...old, pages };
            });
            break;
        }
        case ResourceOperation.UPDATE:
        case ResourceOperation.UPDATE_STATUS: {
            if (!isNotificationInCache(client, evt.resourceId)) {
                break;
            }
            coalescedApi = api;
            coalescedClient = client;
            pendingUpdateIds.add(evt.resourceId);
            if (coalesceTimer !== undefined) {
                clearTimeout(coalesceTimer);
            }
            coalesceTimer = setTimeout(flushCoalescedUpdates, COALESCE_WINDOW_MS);
            break;
        }
    }
};
